import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Download, LogOut, Inbox } from "lucide-react";
import { adminApi, clearAdminSession } from "@/lib/adminApi";
import { RecordRow } from "@/components/admin/RecordRow";
import { MembersPanel } from "@/components/admin/MembersPanel";
import { Seo } from "@/components/Seo";

const TABS = [
  { id: "captain", label: "Captains" },
  { id: "first_mate", label: "First Mates" },
  { id: "yacht", label: "Yachts" },
  { id: "inquiry", label: "Charter Requests" },
  { id: "members", label: "Email List" },
];

export default function AdminInbox() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [tab, setTab] = useState("captain");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    adminApi.get("/auth/me")
      .then((r) => { if (!r.data.is_admin) throw new Error("forbidden"); setUser(r.data); setAuth(true); })
      .catch(() => { setAuth(false); navigate("/admin", { replace: true }); });
  }, [location.state, navigate]);

  useEffect(() => {
    if (auth !== true) return;
    adminApi.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [auth, rows.length]);

  useEffect(() => {
    if (auth !== true || tab === "members") return;
    setLoading(true);
    const url = tab === "inquiry" ? "/admin/inquiries" : `/admin/applications?kind=${tab}`;
    adminApi.get(url).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [auth, tab]);

  const exportCsv = async () => {
    const kind = tab === "inquiry" ? "inquiries" : tab;
    const r = await adminApi.get(`/admin/export.csv?kind=${kind}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `captains-cut-${kind}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const logout = async () => {
    await adminApi.post("/auth/logout").catch(() => {});
    clearAdminSession();
    navigate("/admin", { replace: true });
  };

  if (auth !== true) {
    return <div className="min-h-screen bg-[#070A0F] flex items-center justify-center"><p className="font-coords text-[10px] tracking-[0.4em] text-[#D4AF37]">CHECKING ACCESS…</p></div>;
  }

  const count = (id) => (id === "inquiry" ? stats?.inquiries : id === "members" ? stats?.members : stats?.applications?.[id]) ?? "";

  return (
    <div className="min-h-screen bg-[#070A0F] text-[#F7F8F9] grain" data-testid="admin-inbox-page">
      <Seo title="Applications Inbox — Captain's Cut" description="Private review of Captain, First Mate and Yacht applications and charter requests." path="/admin/inbox" noindex />
      <header className="border-b border-[#D4AF37]/15 bg-[#070A0F]/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="font-display text-sm tracking-[0.28em] gold-text" data-testid="admin-wordmark">CAPTAIN'S CUT · INBOX</Link>
          <div className="flex items-center gap-4 text-[11px] tracking-[0.15em] uppercase">
            <span className="hidden sm:block text-[#94A3B8] normal-case" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} className="flex items-center gap-1 text-[#E8C15A] hover:text-[#F3E08A] transition-colors" data-testid="admin-logout-btn">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <p className="text-[10px] tracking-[0.45em] uppercase text-[#D4AF37]">Private · Team only</p>
        <h1 className="font-display text-2xl md:text-4xl tracking-[0.08em] text-[#F5F3EF] mt-3">APPLICATIONS &amp; CHARTER REQUESTS</h1>
        <p className="text-sm text-[#94A3B8] mt-3 max-w-2xl">
          Full private detail — license numbers, owner names and contact emails live only here. {stats && `${stats.members} verified crew members so far.`}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-10">
          <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`text-[11px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors duration-300 ${
                  tab === t.id ? "border-[#D4AF37] text-[#070A0F] bg-[#D4AF37]" : "border-[#D4AF37]/25 text-[#94A3B8] hover:text-[#E8C15A] hover:border-[#D4AF37]/60"
                }`}
                data-testid={`admin-tab-${t.id}`}>
                {t.label} <span className="font-coords ml-1" data-testid={`admin-count-${t.id}`}>{count(t.id)}</span>
              </button>
            ))}
          </div>
          <button onClick={exportCsv} disabled={rows.length === 0 || tab === "members"}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#E8C15A] border border-[#D4AF37]/40 px-5 py-2.5 hover:bg-[#D4AF37]/10 transition-colors duration-300 disabled:opacity-40"
            data-testid="admin-export-btn">
            <Download size={13} /> Export CSV
          </button>
        </div>

        {tab === "members" && <div className="mt-8"><MembersPanel onChanged={() => adminApi.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {})} /></div>}
        <ul className="space-y-3 mt-8" data-testid="admin-records" hidden={tab === "members"}>
          {loading && <li className="font-coords text-[10px] tracking-[0.3em] text-[#94A3B8]">LOADING…</li>}
          {!loading && rows.length === 0 && tab !== "members" && (
            <li className="border border-dashed border-[#D4AF37]/25 p-10 flex flex-col items-center gap-3 text-[#94A3B8]" data-testid="admin-empty">
              <Inbox size={22} className="text-[#D4AF37]/60" />
              <span className="font-editorial italic">Nothing in this tray yet.</span>
            </li>
          )}
          {rows.map((r) => <RecordRow key={r.id} r={r} kind={tab} />)}
        </ul>
      </main>
    </div>
  );
}
